import type {
  Facet,
  FacetOption,
  RecommendationsResultType,
  SortOption,
} from '@constructor-io/constructorio-client-javascript'
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import { ConstructorLabel, ConstructorLabelNames } from '~/types/Constructor'

export function mapSfccProductDataToProductTile(
  product: FlattenedProduct,
  locale: Locales,
): Omit<ProductTileProps, 'index'> {
  const { wishlist } = useWishlistStore()
  const {
    id,
    name,
    tieredPrices,
    images,
    variationValues,
    slugUrl,
    c_isClearance,
    c_arTryOn,
    c_isSaleEvent,
    c_saleEvent,
    productPromotions,
    c_bvAverageRating,
    c_bvReviewCount,
  } = product
  const angle1 = findImage(images, variationValues, angles[0]) || {
    disBaseLink: images?.[0]?.disBaseLink,
    alt: name,
    link: images?.[0]?.link,
  }
  const angle8 = findImage(images, variationValues, angles[1])
  const angle7 = findImage(images, variationValues, angles[2])
  const angle2 = findImage(images, variationValues, angles[3])
  const angle3 = findImage(images, variationValues, angles[4])
  const lifestyleImage = angle8 || angle7 || angle2 || angle3
  const imagesToMap = [angle1, lifestyleImage].filter((image) => image !== undefined)
  const srcSetImages = generateImageSrcSet(imagesToMap)

  const pdpUrl = getProductUrl({ productId: id, productName: name, locale, slugUrl })
  const isInWishlist = wishlist.some((item) => item.productId === id)
  const promotions = productPromotions?.map((promo) => {
    return { ...promo, id: promo.promotionId }
  })

  return {
    id,
    name,
    rating: Number(c_bvAverageRating || 0),
    ratingCount: Number(c_bvReviewCount || 0),
    prices: tieredPrices ?? [],
    defaultImage: srcSetImages[0],
    lifestyleImage: srcSetImages[1],
    isClearance: c_isClearance,
    tryOnEnabled: c_arTryOn,
    badgeText: c_isSaleEvent ? [c_saleEvent] : [],
    pdpUrl,
    promotions,
    isInWishlist,
    constructorItemId: undefined,
    constructorStrategyId: undefined,
    productData: {
      //  @TODO revist this after datalayer setup ticket completed, thoughts are we should extract this to a
      //  function which can be used in all context to map product data to datalayerproduct or at a minimum multiple functions for each attribute so
      //  the data is consistent each time it is passed back and based on our context we can pass values to said functions
      id,
    },
  }
}

export function mapConstructorProductDataToProductTile(
  mhjEnvironment: string = 'prod',
  product: Partial<RecommendationsResultType>,
): Omit<ProductTileProps, 'index'> {
  const { wishlist } = useWishlistStore()

  const labels = product.labels
  // SAFETY: Constructor types `labels` as an open record; the storefront index only ever
  // populates it with the ConstructorLabel shape, and mapConstructorLabel tolerates missing keys.
  const badgeLabels = labels ? mapConstructorLabel(labels as ConstructorLabel) : []
  // SAFETY: `data` carries the catalogue's custom metadata, which Constructor types as unknown.
  // Its shape is owned by our own index feed and mirrored by ConstructorProductData.
  const data: ConstructorProductData = product.data as ConstructorProductData
  const {
    id: constructorItemId,
    image_urls: imageURLs,
    image_url: imageURL,
    isSaleEvent,
    price,
    facets,
    url,
    isClearance,
    arTryOn,
    variation_id,
    promotions,
    swatches,
  } = data
  const mHJPrimaryCategory = facets?.find((facet) => facet.name === 'mHJPrimaryCategory')?.values[0] || ''
  const mHJRetailCategory = facets?.find((facet) => facet.name === 'mHJRetailCategory')?.values[0] || ''
  const inventoryATS = facets?.find((facet) => facet.name === 'inventoryATS')?.values[0] || ''
  const rating = Number(facets?.find((facet) => facet.name === 'bvAverageRating')?.values[0] || 0)
  const ratingCount = Number(facets?.find((facet) => facet.name === 'bvReviewCount')?.values[0] || 0)
  const metalColour = facets?.find((facet) => facet.name === 'metalColour')?.values[0] || ''
  const saleEvent = isSaleEvent && facets?.find((facet) => facet.name === 'saleEvent')?.values[0]
  if (saleEvent) {
    badgeLabels.push(saleEvent)
  }

  const imageBaseURL = getSfccImageUrl(mhjEnvironment, true)
  const imagesToMap = buildImageUrls({
    defaultImageUrl: imageURL,
    imageUrls: imageURLs,
    productName: product.value || '',
    sfccBaseImageUrl: imageBaseURL,
  })

  const srcSetImages = generateImageSrcSet(imagesToMap)

  const originalPrice = price?.type === 'double' ? price?.retailPrice : price?.amount

  const isInWishlist = wishlist.some((item) => item.productId === variation_id)

  const mappedAndFilteredSwatches = []
  if (swatches && swatches.length > 1) {
    const swatchColourSet = new Set<string>()

    // we must ensure the current variation swatch is first in the list as this is the default swatch for that product tile and if multiple of same colour swatches
    // exist we want to ensure that we can correctly compare the first swatch.id with the current product tile id so it will accurately show as default selected state
    const currentVariationSwatchIndex = swatches.findIndex((swatch) => swatch.id === variation_id)
    if (currentVariationSwatchIndex > 0) {
      swatches.unshift(swatches.splice(currentVariationSwatchIndex, 1)[0])
    }

    for (const swatch of swatches) {
      // Edge case I ran into and only really see this happening in dev but if the current variation is out of stock we keep it in the list as this is the default swatch
      if (swatch.inventory <= 0 && swatch.id !== variation_id) {
        continue
      }

      const swatchColourKeys = Object.keys(swatch.colour)

      if (swatchColourKeys.length === 0) {
        continue
      }

      const colourKey = `${swatchColourKeys[0]}:${swatch.colour[swatchColourKeys[0]]}`

      if (swatchColourSet.has(colourKey)) {
        continue
      }

      swatchColourSet.add(colourKey)
      const swatchSrcSetImages = generateImageSrcSet(
        buildImageUrls({
          imageUrls: swatch.image_urls,
          productName: product.value || '',
          sfccBaseImageUrl: imageBaseURL,
        }),
      )

      const newSwatch = {
        ...swatch,
        defaultImage: swatchSrcSetImages[0],
        lifestyleImage: swatchSrcSetImages[1],
        allImages: swatchSrcSetImages,
      }

      mappedAndFilteredSwatches.push(newSwatch)
    }
  }

  return {
    id: variation_id,
    name: product.value,
    rating,
    ratingCount,
    prices: {
      ...price,
      isFromConstructor: true,
      mhjAudMemberPrices: price?.memberPrice,
    },
    defaultImage: srcSetImages[0],
    lifestyleImage: srcSetImages[1],
    allImages: srcSetImages,
    pdpUrl: url,
    isClearance,
    tryOnEnabled: arTryOn,
    badgeText: badgeLabels,
    promotions,
    isInWishlist,
    constructorItemId,
    constructorStrategyId: product.strategy?.id || undefined,
    swatches: mappedAndFilteredSwatches.length > 1 ? mappedAndFilteredSwatches : undefined,
    productData: {
      //  @TODO revist this after datalayer setup ticket completed, thoughts are we should extract this to a
      //  function which can be used in all context to map product data to datalayerproduct or at a minimum multiple functions for each attribute so
      //  the data is consistent each time it is passed back and based on our context we can pass values to said functions
      id: variation_id,
      name: product.value,
      price: price.amount,
      category: `${mHJRetailCategory}/${mHJPrimaryCategory}`,
      sku: variation_id,
      memberPrice: price.type === 'member' ? price.memberPrice : 0,
      originalPrice,
      brand: 'Michael Hill',
      colour: metalColour,
      badges: badgeLabels,
      rating: rating === 0 ? undefined : rating,
      stockStatus: inventoryATS ? 'In Stock' : 'Out of Stock',
      url,
      imageUrl: srcSetImages[0]?.src || '',
    },
  }
}

export function mapConstructorLabel(label: ConstructorLabel) {
  const { t, te } = useNuxtApp().$i18n
  const labels: string[] = []
  Object.keys(label).forEach((key: ConstructorLabelNames) => {
    // Only labels we carry copy for are shown; Constructor also sends ones Trade doesn't want.
    const name = label[key].display_name
    if (name && te(`constructor_labels.${name}`)) {
      labels.push(t(`constructor_labels.${name}`))
    }
  })
  return labels
}

export const mapConstructorFacets = (facet: Partial<Facet>) => {
  if (facet.name === 'price') {
    return {
      attributeId: facet.name,
      label: facet.display_name,
      values: [
        // Range facets have no hit count; it must stay undefined so the UI renders the slider
        // instead of treating it as a zero-count checkbox option.
        {
          hitCount: undefined,
          label: `${facet.min} - ${facet.max}`,
          value: `(${facet.min}..${facet.max})`,
          selected: false,
        },
      ],
    }
  }

  return {
    attributeId: facet.name,
    label: facet.display_name,
    values: facet.options?.map((item: FacetOption) => ({
      hitCount: item.count,
      label: item.display_name,
      presentationId: item.display_name,
      value: item.value,
      selected: false,
    })),
  }
}

export const mapConstructorSortOptions = (sortOption: Partial<SortOption>) => {
  return {
    label: sortOption.display_name,
    selected: sortOption.status === 'selected',
    value: sortOption.sort_by,
    sortOrder: sortOption.sort_order,
  }
}

export const mapConstructorItemId = (product?: FlattenedProduct | ProductItemWithProductData | null) => {
  if (!product) return ''
  const masterId = product.masterId || product.master?.masterId

  // Preference order: c_groupID > variationGroups[0] > productId
  // https://dev.azure.com/michaelhill/IT%20Global/_wiki/wikis/IT%20Global.wiki/19618/DI-ECOM-F-P-Searchandising-(Constructor.io)-Technical-Project-Specification?anchor=%5C_%5C_groupby
  const groupId = product.c_groupID || product.variationGroups?.[0]?.productId || product.productId || product.id
  return `${masterId}-${groupId}`
}

export const mapConstructorItemIdFromSearch = (searchResult: any[]) =>
  searchResult.map(({ constructorItemId, id, name }: { constructorItemId?: string; id?: string; name?: string }) => ({
    itemId: constructorItemId || id || '',
    variationId: id || '',
    itemName: name || '',
  }))

export const getFilteredFacets = (facets: Partial<Facet>[]) => {
  const filteredFacets: Record<string, string> = {}
  facets.forEach((facet) => {
    facet?.options?.forEach((option: FacetOption) => {
      if (option.status === 'selected' && facet.name) {
        filteredFacets[facet.name] = option.value
      }
    })
  })
  return filteredFacets
}

export const getSelectedAttributes = (
  selectedVariant: FlattenedProduct | ProductItemWithProductData,
  selectedRingSize: string,
) => {
  let selectedAttributes: Array<{ type: string; value: string } | null> = []
  if (selectedVariant.variationValues) {
    selectedAttributes =
      selectedVariant.variationAttributes?.map((attribute: ShopperProductsTypes.VariationAttribute) => {
        const attributeValue = selectedVariant.variationValues?.[attribute.id]
        if (attributeValue) {
          const valueName = attribute.values?.find((av) => av.value === attributeValue)?.name
          return {
            type: attribute.name ?? '',
            value: valueName ?? '',
          }
        }
        return null
      }) || []
  }

  const customRingSize = selectedVariant.c_ringSize ?? selectedVariant.productCustomAttributes?.c_ringSize
  if (customRingSize) {
    // don't override if a value already exists. Unlikely case but the old code also checks for this
    const ringSize = selectedAttributes?.find(
      (selectedAttribute: { type: string | undefined; value: string | undefined } | null) =>
        selectedAttribute?.type === 'Ring Size',
    )
    if (!ringSize) {
      // Safe to add the custom ring size
      selectedAttributes.push({
        type: 'Ring Size',
        value: selectedRingSize,
      })
    }
  }

  return selectedAttributes
}

export const mapProductToReserveInStore = (
  selectedVariant: FlattenedProduct | ProductItemWithProductData,
  selectedRingSize: string,
  locale: Locales,
) => {
  const productId = selectedVariant.id ?? selectedVariant.productId
  const productName = selectedVariant.name ?? selectedVariant.productName

  return {
    reserve_in_store: {
      product: {
        product_sku: productId,
        product_name: productName,
        image_url:
          'id' in selectedVariant
            ? // SAFETY: the `'id' in selectedVariant` guard above narrows this to a FlattenedProduct,
              // which is the only variant shape carrying the image collection.
              findPrimaryProductImage(selectedVariant as FlattenedProduct)?.url
            : selectedVariant.image?.url,
        selected_attributes: getSelectedAttributes(selectedVariant, selectedRingSize),
        product_price: selectedVariant.price, // Validate that this is accurate
        qty: 1,
        product_url: getProductUrl({
          productId: productId,
          productName: productName,
          locale,
          slugUrl: selectedVariant.slugUrl,
        }),
      },
      store_id: useShopStore().myStoreId,
    },
  }
}

const buildImageUrls = ({
  defaultImageUrl,
  imageUrls,
  productName,
  sfccBaseImageUrl,
}: {
  defaultImageUrl?: string
  imageUrls: ConstructorProductData['image_urls']
  productName: string
  sfccBaseImageUrl: string
}) => {
  const defaultImage =
    (imageUrls?.image && sfccBaseImageUrl + imageUrls.image) || (defaultImageUrl && sfccBaseImageUrl + defaultImageUrl)
  const lifeStyleImage = imageUrls?.lifestyleImage && sfccBaseImageUrl + imageUrls.lifestyleImage
  const image3 = imageUrls?.image3 && sfccBaseImageUrl + imageUrls.image3
  const image4 = imageUrls?.image4 && sfccBaseImageUrl + imageUrls.image4
  const image5 = imageUrls?.image5 && sfccBaseImageUrl + imageUrls.image5

  const images = [defaultImage, lifeStyleImage, image3, image4, image5].filter(Boolean)

  return images.map((image) => {
    return { disBaseLink: image, alt: productName, link: image }
  })
}
